import type { HandoffRequest } from '@/contexts/AIContext';
import type { OpenHandoffDto } from '@/lib/connect-api';

export const ESCALATION_TOOL_NAME = 'escalate_to_human';

interface ToolCallEventData {
  tool?: { tool_name?: string | null; tool_call_error?: boolean | null } | null;
}

/**
 * True when an AgentOS SSE run event signals the escalation tool completed the handoff.
 * A handoff that didn't happen fails the tool call, so it doesn't count.
 *
 * Detection keys off the stable `escalate_to_human` tool identifier carried by
 * the `ToolCallCompleted` event — not the agent's free-text reply or a
 * translated confirmation string. This holds across locales and whatever
 * phrasing the model generates.
 */
export function isEscalationCompletedEvent(eventName: string, data: unknown): boolean {
  const call = escalationCall(eventName, data);
  return !!call && !call.tool_call_error;
}

/** True when an AgentOS SSE run event signals the escalation tool was called but the handoff failed. */
export function isEscalationFailedEvent(eventName: string, data: unknown): boolean {
  return !!escalationCall(eventName, data)?.tool_call_error;
}

function escalationCall(eventName: string, data: unknown) {
  if (eventName !== 'ToolCallCompleted') return null;
  const tool = (data as ToolCallEventData | null | undefined)?.tool;
  return tool?.tool_name === ESCALATION_TOOL_NAME ? tool : null;
}

/** What the notice quotes for a request already with the team; null when it names no message. */
export function openHandoffRequest(open: OpenHandoffDto): HandoffRequest | null {
  return open.message && open.sent_at ? { message: open.message, sentAt: new Date(open.sent_at) } : null;
}
